import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Number Crunch — FTUE demo (3 essais progressive intro).
 *
 * E1 : addition simple (8 + 5)
 * E2 : multiplication simple (7 × 6)
 * E3 : mix avec timer
 *
 * Voix Vesperin sobre, anti math-anxiety.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
const DEMO_STEPS = [
    {
        idx: 1,
        titleKey: 'number_crunch.demo.step1_title',
        hintKey: 'number_crunch.demo.step1_hint',
        a: 8,
        b: 5,
        op: 'add',
        answer: 13,
    },
    {
        idx: 2,
        titleKey: 'number_crunch.demo.step2_title',
        hintKey: 'number_crunch.demo.step2_hint',
        a: 7,
        b: 6,
        op: 'mul',
        answer: 42,
    },
    {
        idx: 3,
        titleKey: 'number_crunch.demo.step3_title',
        hintKey: 'number_crunch.demo.step3_hint',
        a: 14,
        b: 9,
        op: 'sub',
        answer: 5,
    },
];
const STAGE_SIZE = 240;
const OP_GLYPH = {
    add: '+',
    sub: '-',
    mul: '×',
    div: '÷',
    estimate: '≈',
};
export function Demo({ level: _level, onComplete, onSkip }) {
    const { t } = useTranslation(['games', 'common']);
    const [stepIdx, setStepIdx] = useState(0);
    const [phase, setPhase] = useState('idle');
    const [buffer, setBuffer] = useState('');
    const [verdict, setVerdict] = useState(null);
    const step = DEMO_STEPS[stepIdx];
    const isLast = stepIdx === DEMO_STEPS.length - 1;
    const startTrial = () => {
        setBuffer('');
        setVerdict(null);
        setPhase('awaiting');
    };
    const handleDigit = (d) => {
        if (phase !== 'awaiting')
            return;
        const newBuffer = `${buffer}${d}`;
        if (newBuffer.length > 4)
            return;
        setBuffer(newBuffer);
        const parsed = Number.parseInt(newBuffer, 10);
        if (Number.isFinite(parsed) && parsed === step.answer) {
            setVerdict('correct');
            setPhase('feedback');
        }
    };
    const handleClear = () => setBuffer('');
    const handleGiveUp = () => {
        setVerdict('incorrect');
        setPhase('feedback');
    };
    const handleNext = () => {
        if (isLast) {
            onComplete();
            return;
        }
        setStepIdx((i) => i + 1);
        setPhase('idle');
        setBuffer('');
        setVerdict(null);
    };
    const equationDisplay = `${step.a} ${OP_GLYPH[step.op]} ${step.b} = ${buffer.length === 0 ? '?' : buffer}`;
    return (_jsxs("div", { className: "flex flex-1 flex-col items-center gap-s4 py-s4 text-center", children: [_jsx("p", { className: "font-mono text-[9.5px] uppercase tracking-[0.30em] text-cyan-text-faint", children: t('common:demo.eyebrow_with_master', { master: t('number_crunch.demo.master') }) }), _jsx("h2", { className: "font-display text-[22px] font-medium leading-7 tracking-cy-display text-cyan-text", children: t(step.titleKey) }), _jsx("p", { className: "max-w-[320px] text-[13.5px] text-cyan-text-secondary", children: t(step.hintKey) }), _jsx("div", { className: "flex items-center justify-center border border-cyan-border-faint bg-cyan-surface", style: { width: STAGE_SIZE, height: STAGE_SIZE }, children: phase === 'feedback' ? (_jsxs("div", { className: "flex flex-col items-center gap-s2 px-s4 text-center", children: [_jsx("span", { className: [
                                'font-display text-[40px] font-medium',
                                verdict === 'correct' ? 'text-cyan-core' : 'text-cyan-media',
                            ].join(' '), children: verdict === 'correct' ? '✓' : '✗' }), _jsx("span", { className: "text-[13px] text-cyan-text", children: `${step.a} ${OP_GLYPH[step.op]} ${step.b} = ${step.answer}` })] })) : (_jsx("span", { className: "font-display text-cyan-text text-center px-s4", style: { fontSize: 32, lineHeight: 1.2 }, children: equationDisplay })) }), phase === 'awaiting' && (_jsxs("div", { className: "grid gap-s2", style: { gridTemplateColumns: 'repeat(3, 56px)' }, children: [[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (_jsx("button", { type: "button", onClick: () => handleDigit(d), className: "flex h-[56px] w-[56px] items-center justify-center bg-cyan-core font-display text-[22px] text-cyan-surface hover:bg-cyan-network active:scale-95", children: d }, `demo-d-${d}`))), _jsx("button", { type: "button", onClick: handleClear, className: "flex h-[56px] w-[56px] items-center justify-center border border-cyan-border bg-cyan-surface font-mono text-[10px] uppercase tracking-[0.20em] text-cyan-text hover:bg-cyan-bg active:scale-95", children: t('number_crunch.buttons.clear') }), _jsx("button", { type: "button", onClick: () => handleDigit(0), className: "flex h-[56px] w-[56px] items-center justify-center bg-cyan-core font-display text-[22px] text-cyan-surface hover:bg-cyan-network active:scale-95", children: "0" }), _jsx("button", { type: "button", onClick: handleGiveUp, className: "flex h-[56px] w-[56px] items-center justify-center border border-cyan-border-faint font-mono text-[9px] uppercase tracking-[0.15em] text-cyan-text-secondary hover:border-cyan-border", children: "?" })] })), _jsxs("div", { className: "flex flex-col items-stretch gap-s3 self-stretch", children: [phase === 'idle' && (_jsx("button", { type: "button", onClick: startTrial, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: t('common:actions.next_trial') })), phase === 'feedback' && (_jsx("button", { type: "button", onClick: handleNext, className: "inline-flex h-12 w-full items-center justify-center bg-cyan-core font-display text-[14px] font-medium tracking-[-0.01em] text-cyan-surface transition active:scale-[0.985] hover:bg-cyan-network", children: isLast ? t('common:actions.start_session') : t('common:actions.next_trial') })), _jsx("button", { type: "button", onClick: onSkip, className: "text-[12px] text-cyan-text-secondary underline-offset-4 hover:underline", children: t('common:actions.skip_demo') })] })] }));
}
//# sourceMappingURL=demo.js.map